import { useState } from "react";
import { DatePicker } from "@design-system/DatePicker/DatePicker";
import {
    Picker,
    type PickerItemData,
    type PickerTriggerElement,
} from "@design-system/Picker/Picker.lib";
import { DEMO_ENTITIES } from "./fixtures/demoTasks";
import {
    getEntityValueId,
    NO_VALUE_ID,
    TASK_AXIS_VALUES,
    type TaskAxis,
} from "./taskBoardAxes";
import type { TaskActionField, TaskBoardChange } from "./taskBoardActions";
import type { DisableableTrigger } from "@design-system/overlay/triggerButtonProps";

const toPickerItems = (field: TaskAxis): Array<PickerItemData<string>> =>
    TASK_AXIS_VALUES[field].map(({ id, title, icon }) => ({
        value: id,
        label: title,
        leading: icon,
    }));

const PICKER_ITEMS: Record<TaskActionField, Array<PickerItemData<string>>> = {
    assignee: toPickerItems("assignee"),
    status: toPickerItems("status"),
    priority: toPickerItems("priority"),
};

const ENTITY_ITEMS = toPickerItems("entity").filter(
    ({ value }) => value !== NO_VALUE_ID,
);

interface TaskFieldPickerProps {
    field: TaskActionField;
    /** The current value id, checked in the list; null for several tasks. */
    valueId: string | null;
    trigger: PickerTriggerElement;
    onChange: (change: TaskBoardChange) => void;
    /** Controls the popup, for a picker a shortcut opens; unset, the trigger does. */
    isOpen?: boolean;
    onOpenChange?: (isOpen: boolean) => void;
}

/**
 * The Assign, Status or Priority picker of the board's action set, shared
 * by the command bar, the card chips and the card shortcuts. Picking the
 * current value changes nothing.
 */
export const TaskFieldPicker = ({
    field,
    valueId,
    trigger,
    onChange,
    isOpen,
    onOpenChange,
}: Readonly<TaskFieldPickerProps>) => (
    <Picker.Select
        items={PICKER_ITEMS[field]}
        value={valueId}
        chevron="none"
        searchable={field === "assignee"}
        trigger={trigger}
        open={isOpen}
        onOpenChange={onOpenChange}
        onValueChange={(nextValueId) => {
            if (nextValueId !== null && nextValueId !== valueId) {
                onChange({ field, valueId: nextValueId });
            }
        }}
    />
);

interface TaskEntitiesPickerProps {
    entityIds: readonly number[];
    trigger: PickerTriggerElement;
    onChange: (change: TaskBoardChange) => void;
    isOpen?: boolean;
    onOpenChange?: (isOpen: boolean) => void;
}

const toValueIds = (entityIds: readonly number[]) =>
    entityIds.map(getEntityValueId);

/**
 * The task's entities as a searchable checkbox list. Picks apply once it
 * closes, as one change: on a board grouped by entity each pick would move
 * the card between lanes, unmounting the picker under the pointer.
 */
export const TaskEntitiesPicker = ({
    entityIds,
    trigger,
    onChange,
    isOpen,
    onOpenChange,
}: Readonly<TaskEntitiesPickerProps>) => {
    const [draftIds, setDraftIds] = useState<readonly string[] | null>(null);
    const commit = () => {
        setDraftIds(null);
        if (draftIds === null) {
            return;
        }
        const pickedIds = new Set(draftIds);
        const currentIds = new Set(entityIds);
        const nextEntityIds = DEMO_ENTITIES.flatMap(({ id }) =>
            pickedIds.has(getEntityValueId(id)) ? [id] : [],
        );
        if (
            nextEntityIds.length !== currentIds.size ||
            nextEntityIds.some((id) => !currentIds.has(id))
        ) {
            onChange({ field: "entities", entityIds: nextEntityIds });
        }
    };
    return (
        <Picker.Select
            multiple
            items={ENTITY_ITEMS}
            value={[...(draftIds ?? toValueIds(entityIds))]}
            chevron="none"
            searchable
            trigger={trigger}
            open={isOpen}
            onOpenChange={(isNextOpen) => {
                if (!isNextOpen) {
                    commit();
                }
                onOpenChange?.(isNextOpen);
            }}
            onValueChange={setDraftIds}
        />
    );
};

interface TaskDueDatePickerProps {
    /** The current due date; null for several tasks or none. */
    dueDateOnly: string | null;
    trigger: DisableableTrigger;
    onChange: (change: TaskBoardChange) => void;
    onOpenChange?: (isOpen: boolean) => void;
}

/**
 * The Due date picker of the board's action set. It offers Clear only when
 * there's a date, so the command bar, which has no current date, never clears.
 * Picking the current day changes nothing.
 */
export const TaskDueDatePicker = ({
    dueDateOnly,
    trigger,
    onChange,
    onOpenChange,
}: Readonly<TaskDueDatePickerProps>) => (
    <DatePicker
        mode="day"
        closeOnSelect
        value={dueDateOnly ?? undefined}
        trigger={trigger}
        onOpenChange={onOpenChange}
        onValueChange={(nextDueDateOnly) => {
            if ((nextDueDateOnly ?? null) !== dueDateOnly) {
                onChange({
                    field: "dueDate",
                    dueDateOnly: nextDueDateOnly ?? null,
                });
            }
        }}
    />
);
